import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { SettingsProvider, useSettings } from "./context/SettingsContext";
import { useInView, injectFavicons } from "./hooks";
import { fetchBrands, imgUrl, API_BASE } from "./api";
import FranchiseCategories from "./components/FranchiseCategories";
import Testimonials from "./components/Testimonials";
import FAQ from "./components/FAQ";
import BlogsNews from "./components/BlogsNews";
import Footer from "./components/Footer";
import BrandsPage from "./components/BrandsPage";
import AllBlogs from "./components/AllBlogs";
import AboutPage from "./components/AboutPage";
import ContactPage from "./components/ContactPage";
import BrandDetailPage from "./components/BrandDetailPage";
import BlogDetailPage from "./components/BlogDetailPage";
import "./App.css";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Brands & Franchises", href: "/brands" },
  { label: "Contact", href: "/contact" },
  { label: "About", href: "/about" },
  { label: "Blogs", href: "/all-blogs" },
];

// ─── Header ───────────────────────────────────────────────────────────────────
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { getLogo } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const scrollTo = (href) => {
    setMenuOpen(false);
    if (!href.startsWith("#")) {
      navigate(href);
      window.scrollTo({ top: 0 });
      return;
    }
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const isActivePath = (href) => {
    const path = location.pathname;
    if (href === "#home") return path === "/";
    if (href === "/brands")
      return path === "/brands" || path.startsWith("/brands/");
    if (href === "/all-blogs")
      return path === "/all-blogs" || path.startsWith("/blogs/");
    if (href === "/contact") return path === "/contact";
    if (href === "/about") return path === "/about";
    return false;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 py-4 transition-all duration-500 ${scrolled ? "bg-white/60 backdrop-blur-2xl shadow-md border-b border-white/30" : "bg-[#F4EEE6]"}`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <button
          onClick={() => scrollTo("#home")}
          className="flex items-center gap-3"
        >
          <img
            src={getLogo()}
            alt="Franchise Business Solution"
            className="h-12 w-auto"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.href)}
              className="font-body text-[11px] font-bold tracking-[0.12em] uppercase text-[#101010] hover:text-stone-900 px-3 py-2 transition-colors duration-200 relative group"
            >
              {link.label}
              <span
                className={`absolute bottom-1 left-3 right-3 h-[1px] bg-gold transition-transform duration-300 origin-left
        ${isActivePath(link.href) ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}
              />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={() => scrollTo("/contact")}
            className="hidden lg:flex items-center bg-gold hover:bg-gold-dark text-white font-body text-[11px] font-semibold tracking-[0.14em] uppercase px-6 py-3 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 rounded-br-xl"
          >
            Book Consultation
          </button>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden flex flex-col gap-1.5 p-2"
            aria-label="Toggle menu"
          >
            <span
              className={`w-6 h-0.5 bg-stone-800 transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-2" : ""}`}
            />
            <span
              className={`w-6 h-0.5 bg-stone-800 transition-all duration-300 ${menuOpen ? "opacity-0" : ""}`}
            />
            <span
              className={`w-6 h-0.5 bg-stone-800 transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`}
            />
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${menuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"}`}
      >
        <nav className="bg-white border-t border-stone-100 mt-[20px] px-6 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.href)}
              className={`text-left font-body text-sm tracking-widest uppercase py-3 border-b border-stone-50 transition-colors relative
      ${isActivePath(link.href) ? "text-gold" : "text-stone-700 hover:text-gold"}`}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo("/contact")}
            className="mt-4 bg-gold text-white font-body text-xs font-semibold tracking-widest uppercase px-6 py-3 hover:bg-gold-dark transition-colors"
          >
            Book Consultation
          </button>
        </nav>
      </div>
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const { getBanner, getHeroHeading, getHeroDesc } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  const heroBanner = getBanner("home");
  const heroHeading = getHeroHeading();
  const heroDesc = getHeroDesc();

  const defaultHeading = [
    "Scaling India's Most",
    "Promising Hospitality",
    "Brands",
  ];
  const headingLines = heroHeading || defaultHeading;

  const defaultDesc =
    "Franchise Business Solution helps ambitious hospitality and lifestyle brands expand strategically through franchise development, investor acquisition, and scalable growth systems.";

  return (
    <section
      id="home"
      className="relative w-full h-screen min-h-[600px] overflow-hidden"
    >
      <img
        src={heroBanner || "images/hero_image.jpg"}
        className="absolute inset-0 w-full h-full object-cover"
        alt="hero"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      <div
        className={`relative z-10 h-full flex flex-col items-center justify-center text-center px-4 md:px-16 lg:px-24 pt-16 transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      >
        <h1
          className="font-display text-white font-normal leading-[1.15] mb-6 w-full"
          style={{ fontSize: "clamp(2rem, 5.5vw, 5.2rem)" }}
        >
          {headingLines.map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < headingLines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h1>

        <p
          className="font-body text-white/70 font-light leading-relaxed max-w-3xl mb-12 px-2 md:px-0"
          style={{ fontSize: "clamp(0.9rem, 1.3vw, 1.05rem)" }}
        >
          {heroDesc || defaultDesc}
        </p>

        <button
          onClick={() => navigate("/contact")}
          className="group relative overflow-hidden border border-gold text-white font-body text-[11px] font-semibold tracking-[0.2em] uppercase px-10 py-4 transition-all duration-300 hover:text-stone-900 rounded-br-xl"
        >
          <span className="relative z-10">Enquire Now</span>
          <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
        </button>
      </div>
    </section>
  );
}

// ─── Brands Section ───────────────────────────────────────────────────────────
function BrandsSection() {
  const [ref, inView] = useInView();
  const [brands, setBrands] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBrands({ featured: true, limit: 16 })
      .then((d) => setBrands(d.data || []))
      .catch(() => setBrands([]));
  }, []);

  return (
    <section
      id="brands"
      ref={ref}
      className="bg-cream py-20 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-14">
          <span className="inline-block font-body text-[10px] tracking-[0.25em] uppercase text-[#D29D4E] border border-[#D29D4E] px-3 py-1 rounded-full mb-4">
            Premium Franchise
          </span>
          <div
            className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <h2
              className="font-display font-normal text-stone-900 leading-[1.1]"
              style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.8rem)" }}
            >
              Curating India's Most
              <br />
              Aspirational Hospitality Brands
            </h2>
          </div>
          <div
            className={`flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mt-6 transition-all duration-700 delay-150 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <p className="font-body text-stone-500 text-base leading-relaxed max-w-2xl">
              Franchise Business Solution helps ambitious hospitality and
              lifestyle brands expand strategically through franchise
              development, investor acquisition, and scalable growth systems.
            </p>
            <button
              onClick={() => {
                navigate("/brands");
                window.scrollTo({ top: 0 });
              }}
              className="flex items-center gap-2 font-body text-[11px] tracking-[0.12em] uppercase text-stone-700 hover:text-gold transition-colors duration-200 group flex-shrink-0 sm:mt-0 mt-2"
            >
              Explore All Brands
              <span className="w-6 h-6 rounded-full bg-gold group-hover:bg-stone-900 flex items-center justify-center text-white text-xs transition-colors duration-300">
                →
              </span>
            </button>
          </div>
        </div>

        {brands.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {brands.map((brand, i) => (
              <div
                key={brand._id}
                onClick={() => navigate(`/brands/${brand.slug}`)}
                className={`relative overflow-hidden rounded-br-[40px] cursor-pointer group transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{
                  transitionDelay: `${200 + i * 100}ms`,
                  aspectRatio: "4/5",
                }}
              >
                <img
                  src={
                    brand.heroImg ? imgUrl(brand.heroImg) : "images/french1.jpg"
                  }
                  alt={brand.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/60" />
                <div className="absolute top-4 left-4">
                  <span className="font-body text-white/90 text-[10px] tracking-[0.15em] uppercase bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    {brand.category?.name || ""}
                  </span>
                </div>
                <div className="absolute inset-0 flex items-center justify-center px-3 z-10">
                  {brand.logo ? (
                    <img
                      src={imgUrl(brand.logo)}
                      alt={brand.name}
                      className="w-[75%] h-[75%] max-w-[260px] object-contain filter brightness-110 drop-shadow-2xl"
                    />
                  ) : (
                    <p className="font-display text-white text-xl font-light text-center px-2">
                      {brand.name}
                    </p>
                  )}
                </div>
                <div className="absolute inset-0 border border-gold/0 group-hover:border-gold/60 transition-all duration-300" />
              </div>
            ))}
          </div>
        ) : (
          // Skeleton while loading
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="rounded-br-[40px] bg-stone-100 animate-pulse"
                style={{ aspectRatio: "3/4" }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Why FBS Section ──────────────────────────────────────────────────────────
function WhyFBSSection() {
  const [ref, inView] = useInView();
  const { getAboutFBSImage, getAboutFBSDesc } = useSettings();
  const navigate = useNavigate();

  const aboutImg = getAboutFBSImage();
  const aboutDesc = getAboutFBSDesc();
  const defaultDesc = [
    "Franchise Business Solution helps ambitious hospitality and lifestyle brands expand strategically through franchise development, investor acquisition, and scalable growth systems.",
  ];

  return (
    <section
      id="about"
      ref={ref}
      className="bg-white py-20 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div
          className={`transition-all duration-700 ${inView ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
        >
          <span className="inline-block font-body text-[10px] tracking-[0.25em] uppercase text-[#D29D4E] border border-[#D29D4E] px-3 py-1 rounded-full mb-4">
            About FBS
          </span>
          <h2
            className="font-display font-normal text-stone-900 leading-tight mb-6"
            style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}
          >
            Why FBS?
          </h2>
          <div className="space-y-3 mb-10">
            {(aboutDesc || defaultDesc).map((para, i) => (
              <p
                key={i}
                className="font-body text-stone-500 text-base leading-relaxed max-w-md"
              >
                {para}
              </p>
            ))}
          </div>
          <button
            onClick={() => {
              window.scrollTo({ top: 0 });
              navigate("/about");
            }}
            className="group relative overflow-hidden border border-gold text-stone-800 font-body text-[10px] font-semibold tracking-[0.22em] uppercase px-8 py-3.5 transition-all duration-300 hover:text-white rounded-br-xl"
          >
            <span className="relative z-10">Learn More About Us</span>
            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
          </button>
        </div>

        <div
          className={`transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
        >
          <div className="relative">
            <div className="absolute -bottom-4 -right-4 w-full h-full border border-gold/30 rounded-br-[40px]" />
            <img
              src={aboutImg || "images/about_us.jpg"}
              alt="FBS Hospitality"
              className="relative z-10 w-full h-[500px] object-cover rounded-br-[40px]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function MainPage() {
  return (
    <>
      <Header />
      <HeroSection />
      <BrandsSection />
      <WhyFBSSection />
      <FranchiseCategories />
      <Testimonials />
      <FAQ />
      <BlogsNews />
      <Footer />
    </>
  );
}

// ─── Page Wrappers ────────────────────────────────────────────────────────────
const BrandsPageWrapper = () => (
  <>
    <Header />
    <BrandsPage />
    <Footer />
  </>
);
const BlogsPageWrapper = () => (
  <>
    <Header />
    <AllBlogs />
    <Footer />
  </>
);
const AboutPageWrapper = () => (
  <>
    <Header />
    <AboutPage />
    <Footer />
  </>
);
const ContactPageWrapper = () => (
  <>
    <Header />
    <ContactPage />
    <Footer />
  </>
);
const BrandDetailWrapper = () => (
  <>
    <Header />
    <BrandDetailPage />
    <Footer />
  </>
);
const BlogDetailWrapper = () => (
  <>
    <Header />
    <BlogDetailPage />
    <Footer />
  </>
);

// ─── Favicon injector ─────────────────────────────────────────────────────────
function FaviconInjector() {
  const { getFavicons } = useSettings();
  useEffect(() => {
    const favicons = getFavicons();
    if (favicons) injectFavicons(favicons, API_BASE);
  }, [getFavicons]);
  return null;
}

// ─── App Root ─────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <SettingsProvider>
      <BrowserRouter>
        <FaviconInjector />
        <div className="min-h-screen w-full overflow-x-hidden">
          <Routes>
            <Route path="/" element={<MainPage />} />
            <Route path="/brands" element={<BrandsPageWrapper />} />
            <Route path="/all-blogs" element={<BlogsPageWrapper />} />
            <Route path="/about" element={<AboutPageWrapper />} />
            <Route path="/contact" element={<ContactPageWrapper />} />
            <Route path="/brands/:slug" element={<BrandDetailWrapper />} />
            <Route path="/blogs/:slug" element={<BlogDetailWrapper />} />
          </Routes>
        </div>
      </BrowserRouter>
    </SettingsProvider>
  );
}
